import useWindowSize from '@hooks/use-window-size';
import { useCallback } from 'react';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { Product } from '@interfaces/models/product';

interface ImpressionProductDetails {
  product_id: string;
  product_brand: string;
  product_unitprice_ati: string;
  product_card_tag: string;
  currency: string;
  seller_id: string;
  product_universe: string;
  product_feed_category: string;
  product_position: string;
}

const getProductTrackingData = (
  product: Product,
): Omit<ImpressionProductDetails, 'product_feed_category' | 'product_position'> => ({
  product_id: String(product.id),
  product_brand: product.brand?.name,
  product_unitprice_ati:
    product.price?.cents !== undefined && product.price?.cents !== null ? String(product.price.cents / 100) : '',
  product_card_tag: product.tags?.join(',') ?? '',
  currency: product.price?.currency ?? '',
  seller_id: product.seller?.id ? product.seller?.id : '',
  product_universe: 'universeId' in product ? String(product.universeId) : '',
});

export const getRowCount = ({
  isWiderThanXl,
  isWiderThanLg,
  isWiderThanMd,
}: {
  isWiderThanXl: boolean;
  isWiderThanLg: boolean;
  isWiderThanMd: boolean;
}) => {
  if (isWiderThanXl) {
    return 5;
  }
  if (isWiderThanLg) {
    return 4;
  } else if (isWiderThanMd) {
    return 3;
  }
  return 2;
};

const useProductFeedImpressionTracking = (productFeedCategory?: string, trackingPayload?: Record<string, unknown>) => {
  const { isWiderThanMd, isWiderThanLg, isWiderThanXl, hasWindowSizeBeenSet } = useWindowSize();
  const rowCount = getRowCount({ isWiderThanXl, isWiderThanLg, isWiderThanMd });

  const { sendAnalyticEvent } = useAnalyticEvents('product');

  // this fn is used to send product impressions events on scroll
  const sendProductImpressionsEvent = useCallback(
    (productCardDetails: ImpressionProductDetails[]) => {
      if (productCardDetails.length === 0) {
        return;
      }

      const sendTrackingEvents = () =>
        sendAnalyticEvent('product_impression', {
          ...(trackingPayload || {}),
          product_card_details: productCardDetails,
        });

      // using requestIdleCallback to avoid blocking the main thread
      window.requestIdleCallback(sendTrackingEvents);
    },
    [sendAnalyticEvent, trackingPayload],
  );

  const onRowReveal = useCallback(
    (index: number, productsOnRow: Product[]) => {
      const newProductData = productsOnRow.map((product, i) => ({
        ...getProductTrackingData(product),
        product_feed_category: productFeedCategory,
        product_position: String(index + i + 1),
      }));
      sendProductImpressionsEvent(newProductData);
    },
    [productFeedCategory],
  );

  return { onRowReveal, rowCount, hasWindowSizeBeenSet };
};

export default useProductFeedImpressionTracking;
